import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const ScrollLink = ({ to, children, className, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionId = to.replace('/#', '');

  const handleClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
    onClick?.();
  };

  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

const Header = ({ rightSlot }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navLinkClass = "text-stone-700 hover:text-blue-900 transition-colors duration-200 font-medium";
  const mobileLinkClass = "block w-full text-left text-stone-700 hover:text-blue-900 transition-colors py-2";

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <nav className="bg-white/95 backdrop-blur-sm border-b border-stone-200 px-6 py-4">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/api/img/allfood-logo-48.webp"
              srcSet="/api/img/allfood-logo-48.webp 1x, /api/img/allfood-logo-96.webp 2x"
              alt="ALL-FOOD Logo"
              className="h-12 w-12 logo-animation"
              data-testid="header-logo"
              width="48"
              height="48"
              fetchPriority="high"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <ScrollLink to="/#about" className={navLinkClass}>About</ScrollLink>
            {/* Services dropdown (hover on desktop) */}
            <div className="relative group" data-testid="services-menu">
              <Link to="/services" className={navLinkClass + " inline-flex items-center gap-1"} data-testid="services-menu-hub">
                Services
                <span aria-hidden="true" className="text-xs">▾</span>
              </Link>
              <div
                className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-2 z-50"
                role="menu"
              >
                <div className="min-w-[220px] bg-white border border-stone-200 rounded-md shadow-lg py-2">
                  <Link to="/services/strategic-sourcing" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="services-menu-strategic-sourcing">Strategic Sourcing</Link>
                  <Link to="/private-label" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="services-menu-private-label">Private Label</Link>
                </div>
              </div>
            </div>
            {/* Products dropdown (hover on desktop) */}
            <div className="relative group" data-testid="products-menu">
              <Link to="/categories" className={navLinkClass + " inline-flex items-center gap-1"} data-testid="products-menu-hub">
                Products
                <span aria-hidden="true" className="text-xs">▾</span>
              </Link>
              <div
                className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-2 z-50"
                role="menu"
              >
                <div className="min-w-[220px] bg-white border border-stone-200 rounded-md shadow-lg py-2">
                  <Link to="/categories/italian-olive-oil" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="products-menu-italian-olive-oil">Italian Olive Oil</Link>
                  <Link to="/categories/italian-pasta" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="products-menu-italian-pasta">Italian Pasta</Link>
                  <Link to="/categories/italian-pastry-bakery" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="products-menu-italian-pastry-bakery">Italian Pastry &amp; Bakery</Link>
                  <Link to="/categories/italian-cheese" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="products-menu-italian-cheese">Italian Cheese</Link>
                  <Link to="/categories/italian-cured-meats" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="products-menu-italian-cured-meats">Italian Cured Meats</Link>
                </div>
              </div>
            </div>
            {/* Brands dropdown (hover on desktop) */}
            <div className="relative group" data-testid="brands-menu">
              <Link to="/brands" className={navLinkClass + " inline-flex items-center gap-1"} data-testid="brands-menu-hub">
                Brands
                <span aria-hidden="true" className="text-xs">▾</span>
              </Link>
              <div
                className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-2 z-50"
                role="menu"
              >
                <div className="min-w-[220px] bg-white border border-stone-200 rounded-md shadow-lg py-2">
                  <Link to="/brands/san-chimento" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="brands-menu-san-chimento">San Chimento</Link>
                  <Link to="/brands/le-dolci-sorelle" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="brands-menu-le-dolci-sorelle">Le Dolci Sorelle</Link>
                  <Link to="/brands/italiana-formaggi" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="brands-menu-italiana-formaggi">Italiana Formaggi</Link>
                </div>
              </div>
            </div>
            {/* Markets dropdown (hover on desktop) */}
            <div className="relative group" data-testid="markets-menu">
              <Link to="/markets" className={navLinkClass + " inline-flex items-center gap-1"} data-testid="markets-menu-hub">
                Markets
                <span aria-hidden="true" className="text-xs">▾</span>
              </Link>
              <div
                className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-2 z-50"
                role="menu"
              >
                <div className="min-w-[220px] bg-white border border-stone-200 rounded-md shadow-lg py-2">
                  <Link to="/markets/usa" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="markets-menu-usa">USA</Link>
                  <Link to="/markets/europe" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="markets-menu-europe">Europe</Link>
                  <Link to="/markets/uk" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="markets-menu-uk">UK</Link>
                  <Link to="/markets/canada" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="markets-menu-canada">Canada</Link>
                  <Link to="/markets/australia" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="markets-menu-australia">Australia</Link>
                  <Link to="/markets/middle-east" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="markets-menu-middle-east">Middle East</Link>
                  <Link to="/markets/south-america" className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-900" data-testid="markets-menu-south-america">South America</Link>
                </div>
              </div>
            </div>
            <Link to="/blog" className={navLinkClass} data-testid="header-blog-link">Blog</Link>
            <Link to="/for-producers" className={navLinkClass}>For Producers</Link>
            <Button asChild className="bg-blue-900 hover:bg-blue-950 text-white transition-all duration-300 shadow-md hover:shadow-lg">
              <ScrollLink to="/#contact" data-testid="header-contact-btn">Contact Us</ScrollLink>
            </Button>
            {rightSlot && rightSlot}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-stone-700 hover:text-blue-900 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pt-4 pb-2 space-y-3 border-t border-stone-200 mt-4" data-testid="mobile-menu">
            <ScrollLink to="/#about" className={mobileLinkClass} onClick={() => setIsMenuOpen(false)}>About</ScrollLink>
            <Link to="/services" className={mobileLinkClass} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-all-services">Services</Link>
            <Link to="/services/strategic-sourcing" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-strategic-sourcing">↳ Strategic Sourcing</Link>
            <Link to="/private-label" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-private-label">↳ Private Label</Link>
            <span className={mobileLinkClass + " cursor-default"}>Products</span>
            <Link to="/categories/italian-olive-oil" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-italian-olive-oil">↳ Italian Olive Oil</Link>
            <Link to="/categories/italian-pasta" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-italian-pasta">↳ Italian Pasta</Link>
            <Link to="/categories/italian-pastry-bakery" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-italian-pastry-bakery">↳ Italian Pastry &amp; Bakery</Link>
            <Link to="/categories/italian-cheese" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-italian-cheese">↳ Italian Cheese</Link>
            <Link to="/categories/italian-cured-meats" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-italian-cured-meats">↳ Italian Cured Meats</Link>
            <span className={mobileLinkClass + " cursor-default"}>Brands</span>
            <Link to="/brands/san-chimento" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-san-chimento">↳ San Chimento</Link>
            <Link to="/brands/le-dolci-sorelle" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-le-dolci-sorelle">↳ Le Dolci Sorelle</Link>
            <Link to="/brands/italiana-formaggi" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-italiana-formaggi">↳ Italiana Formaggi</Link>
            <span className={mobileLinkClass + " cursor-default"}>Markets</span>
            <Link to="/markets/usa" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-markets-usa">↳ USA</Link>
            <Link to="/markets/europe" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-markets-europe">↳ Europe</Link>
            <Link to="/markets/uk" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-markets-uk">↳ UK</Link>
            <Link to="/markets/canada" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-markets-canada">↳ Canada</Link>
            <Link to="/markets/australia" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-markets-australia">↳ Australia</Link>
            <Link to="/markets/middle-east" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-markets-middle-east">↳ Middle East</Link>
            <Link to="/markets/south-america" className={mobileLinkClass + " pl-4 text-sm"} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-markets-south-america">↳ South America</Link>
            <Link to="/blog" className={mobileLinkClass} onClick={() => setIsMenuOpen(false)} data-testid="mobile-menu-blog-link">Blog</Link>
            <Link to="/for-producers" className={mobileLinkClass} onClick={() => setIsMenuOpen(false)}>For Producers</Link>
            <Button asChild className="w-full bg-blue-900 hover:bg-blue-950 text-white">
              <ScrollLink to="/#contact" onClick={() => setIsMenuOpen(false)}>Contact Us</ScrollLink>
            </Button>
            {rightSlot && <div className="pt-1">{rightSlot}</div>}
          </div>
        )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
